import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import ReactModal from 'react-modal'
import ReactPlayer from 'react-player'
import { useParams } from 'react-router-dom'

import type { ChangeEvent, ReactElement } from 'react'
import type { IVideoResultItem, VIDEO_CATEGORIES } from '~/types/video'

import UpdateModal from '../Modals/UpdateModal'
import { useUserVideosListQuery, keys, useDynamicCategoryVideosListQuery } from '../queries'
import VideoThumbnail from '../VideoThumbnail'
import VideoThumbnailSkeleton from '../VideoThumbnail/skeleton'

import BackNavigation from '~/components/BackNavigation'
import ContentLayout from '~/components/ContentLayout'
import Filters, { FiltersContent } from '~/components/Filters'
import SearchInput from '~/components/Inputs/Basic/SearchInput'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import ViewTitle from '~/components/ViewTitle'
import { debounce } from '~/utils/function'
import { filter, queryConfig } from '~/utils/videos'

import './styles.scss'

const CategoryDetails = (): ReactElement => {
  /** Local state */

  const { category } = useParams<{ category: VIDEO_CATEGORIES }>()

  const { apiFn } = queryConfig[category as VIDEO_CATEGORIES]

  const queryClient = useQueryClient()

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)

  const [videoToUpdate, setVideoToUpdate] = useState<IVideoResultItem>()

  const [search, setSearch] = useState<string>('')

  const [isPlayerOpen, setIsPlayerOpen] = useState(false)

  const [activeVideoUrl, setActiveVideoUrl] = useState<string>()

  const [videos, setVideos] = useState<IPaginatedResponse<IVideoResultItem>>()

  /** Queries & Mutations */

  const { data: favouriteVideos } = useUserVideosListQuery({ search })

  const { data, isLoading } = useDynamicCategoryVideosListQuery(category as VIDEO_CATEGORIES, apiFn, search)

  /** Effects */

  useEffect(() => {
    if (data) {
      if (data?.data && search) {
        setVideos({
          ...data,
          data: data?.data?.filter(({ tags, title }) => {
            return filter(title, tags, search)
          }),
        })
      } else {
        setVideos(data)
      }
    }
  }, [search, data])

  /** Handlers */

  const isFavourite = (videoId: string): boolean => {
    if (favouriteVideos) {
      return !!favouriteVideos.data?.find(({ id }) => id === videoId)
    }
    return false
  }

  const debouncedSearch = debounce((search) => setSearch(search), 500)

  const handleInvalidateQuery = () =>
    queryClient.invalidateQueries({ queryKey: keys.dynamic(category as VIDEO_CATEGORIES) })

  const handleSearchChanged = (e: ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value)
  }

  const handleToggleUpdateModal = (isOpen: boolean) => {
    return () => setIsUpdateModalOpen(isOpen)
  }

  const handleEditVideo = (video: IVideoResultItem) => {
    setVideoToUpdate(video)
    setIsUpdateModalOpen(true)
  }

  const handleThumbnailClick = (videoUrl: string) => {
    setActiveVideoUrl(videoUrl)
    setIsPlayerOpen(true)
  }

  const handleClosePlayer = () => {
    setIsPlayerOpen(false)
    setActiveVideoUrl(undefined)
  }

  const getTitle = (): string => {
    switch (category) {
      case 'my-list':
        return 'My list'
      case 'latest':
        return 'Latest videos'
      case 'conferences':
        return 'Conferences'
      case 'tutorials':
        return 'Intranet tutorials'
      case 'other':
        return 'Other'
      default:
        return ''
    }
  }

  /** Render */

  return (
    <>
      <ViewTitle className="videos-category-details-header">
        <BackNavigation title="Back" to="/videos" />
        <div className="videos-category-title">{getTitle()}</div>
        <div className="page-extra" />
      </ViewTitle>
      <Filters className="videos-filters-details">
        <FiltersContent className="flex">
          <SearchInput className="table-search-bar" disabled={isLoading} onChange={handleSearchChanged} />
        </FiltersContent>
      </Filters>
      <Layout className="videos-category-details-view" withPadding>
        <ContentLayout
          className={classNames('videos-category-details-view-content-layout', { 'is-loading': isLoading })}
        >
          <Section className="section">
            <div className="videos-container">
              {isLoading
                ? [...Array(Math.floor(Math.random() * 4) + 2).keys()].map((__, index) => (
                    <div className="video-thumbnail-wrapper" key={index}>
                      <VideoThumbnailSkeleton key={index} />
                    </div>
                  ))
                : videos?.data?.map((video) => {
                    const {
                      created_at,
                      created_by,
                      description,
                      download_url,
                      duration,
                      id,
                      tags,
                      thumbnail_url,
                      title,
                    } = video

                    return (
                      <div className="video-thumbnail-wrapper" key={id}>
                        <VideoThumbnail
                          createdAt={created_at}
                          createdBy={created_by}
                          description={description}
                          duration={duration}
                          favourite={isFavourite(video.id)}
                          onClick={handleThumbnailClick}
                          onEdit={() => handleEditVideo(video)}
                          onFavouriteToggle={handleInvalidateQuery}
                          tags={tags}
                          thumbnailUrl={thumbnail_url}
                          title={title}
                          url={download_url}
                          videoId={id}
                        />
                      </div>
                    )
                  })}
            </div>
          </Section>
        </ContentLayout>
        {videoToUpdate ? (
          <UpdateModal
            isOpen={isUpdateModalOpen}
            onClose={handleToggleUpdateModal(false)}
            onSubmit={handleInvalidateQuery}
            video={videoToUpdate}
          />
        ) : null}
        <ReactModal
          ariaHideApp={false}
          className="video-player-modal"
          isOpen={isPlayerOpen}
          onRequestClose={handleClosePlayer}
          overlayClassName="video-player-overlay"
          shouldCloseOnOverlayClick
        >
          <ReactPlayer controls height="100%" playing url={activeVideoUrl} width="100%" />
        </ReactModal>
      </Layout>
    </>
  )
}

export default CategoryDetails
